import { COUNTRIES } from './countries'
import { US_STATES, US_TERRITORIES } from './usStates'

/**
 * Region labels used by Region Teams (backend regionTeams.service):
 *  - "OH"   a US state or DC
 *  - "PR"   a US territory (PR, VI, GU, AS, MP)
 *  - "C-DE" a country (prefixed, because ISO country codes collide with
 *           state codes: DE, CA, IN, GA…)
 */

export type LocationLike = { country?: string | null; state?: string | null } | null | undefined

let displayNames: Intl.DisplayNames | null = null
try {
  displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
} catch {
  displayNames = null
}

/** Country name from the shared table, then Intl, then the code itself. */
export const countryDisplayName = (code?: string | null): string => {
  if (!code) return ''
  const upper = code.toUpperCase()
  if (COUNTRIES[upper]) return COUNTRIES[upper].name
  try {
    return displayNames?.of(upper) || upper
  } catch {
    return upper
  }
}

/** "OH" → "Ohio", "PR" → "Puerto Rico"; unknown codes come back unchanged. */
export const usRegionDisplayName = (code?: string | null): string => {
  if (!code) return ''
  const upper = code.toUpperCase()
  return US_STATES[upper]?.name || US_TERRITORIES[upper] || upper
}

export const isCountryLabel = (label?: string | null): boolean => !!label && /^C-[A-Z]{2}$/i.test(label)

/** Full display name for a region label ("OH", "PR", "C-DE"). */
export const regionLabelName = (label?: string | null): string => {
  if (!label) return 'Unknown'
  if (isCountryLabel(label)) return countryDisplayName(label.slice(2))
  return usRegionDisplayName(label)
}

/** Compact chip text: states keep their 2-letter code, countries show their name. */
export const regionLabelShort = (label?: string | null): string => {
  if (!label) return '?'
  if (isCountryLabel(label)) return countryDisplayName(label.slice(2))
  return label.toUpperCase()
}

/** Region label for a location / signup snapshot (matches the backend's labelling). */
export const regionLabelOf = (loc: LocationLike): string | null => {
  if (!loc?.country) return null
  const country = loc.country.toUpperCase()
  if (country === 'US') return loc.state ? loc.state.toUpperCase() : null
  return `C-${country}`
}

/** Human name for a location: "Ohio" for US players, "Germany" for everyone else. */
export const locationDisplayName = (loc: LocationLike): string => {
  if (!loc?.country) return ''
  const country = loc.country.toUpperCase()
  if (country === 'US') return loc.state ? usRegionDisplayName(loc.state) : 'United States'
  return countryDisplayName(country)
}

/** "Ohio + Indiana", or "Ohio + 3 more" when long. */
export const formatRegionList = (labels: string[] = [], max = 3): string => {
  const names = labels.map(regionLabelName)
  if (names.length <= max) return names.join(' + ')
  return `${names.slice(0, max).join(' + ')} + ${names.length - max} more`
}

/** Sort key so US states come first (alphabetical), then territories, then countries. */
export const regionSortKey = (label: string): string => {
  if (isCountryLabel(label)) return `2-${regionLabelName(label)}`
  if (US_TERRITORIES[label.toUpperCase()]) return `1-${regionLabelName(label)}`
  return `0-${regionLabelName(label)}`
}
